import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import {
  PlusIcon,
  ShieldCheckIcon,
  XMarkIcon,
  PencilIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';

const fetchUsers = () => api.get('/users').then(res => res.data.data.users);

export default function Users() {
  const queryClient = useQueryClient();
  const { user: currentUser, impersonate } = useAuth();
  const { data: users, isLoading } = useQuery('users', fetchUsers);

  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'billing_officer',
    status: 'active'
  });

  const createUser = useMutation(
    (data) => api.post('/users', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('User created successfully');
        setShowModal(false);
        resetForm();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to create user');
      }
    }
  );

  const updateUser = useMutation(
    (data) => api.put(`/users/${editingUser?.id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('User updated successfully');
        setShowModal(false);
        resetForm();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to update user');
      }
    }
  );

  const resetForm = () => {
    setEditingUser(null);
    setForm({
      username: '',
      email: '',
      password: '',
      role: 'billing_officer',
      status: 'active'
    });
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setForm({
      username: user.username,
      email: user.email,
      password: '', // Keep blank unless resetting
      role: user.role,
      status: user.status
    });
    setShowModal(true);
  };

  const handleAutologinClick = async (user) => {
    if (window.confirm(`Are you sure you want to login as ${user.username}?`)) {
      try {
        toast.info(`Standard login bypass: Redirecting as ${user.username}...`);
        await impersonate(user.id);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to auto login');
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.username || !form.email) {
      toast.error('Username and email are required');
      return;
    }

    if (editingUser) {
      const data = {
        username: form.username,
        email: form.email,
        role: form.role,
        status: form.status
      };
      if (form.password) {
        data.password = form.password;
      }
      updateUser.mutate(data);
    } else {
      if (!form.password) {
        toast.error('Password is required');
        return;
      }
      createUser.mutate(form);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'badge-green',
      inactive: 'badge-gray',
      locked: 'badge-red',
      suspended: 'badge-yellow'
    };
    return styles[status] || 'badge-gray';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Users</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Manage system users and roles</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add User
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>2FA</th>
                <th className="w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isLoading ? (
                <tr><td colSpan="7" className="text-center py-8">Loading...</td></tr>
              ) : users?.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-8 text-slate-500">No users found</td></tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50">
                    <td className="font-medium">{user.username}</td>
                    <td>{user.email}</td>
                    <td className="capitalize">{user.role.replace('_', ' ')}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(user.status)}`}>
                        {user.status}
                      </span>
                    </td>
                    <td>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}</td>
                    <td>
                      {user.twoFactorEnabled ? (
                        <ShieldCheckIcon className="h-5 w-5 text-green-500" />
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleEditClick(user)}
                          className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                          title="Edit User"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        {currentUser?.id !== user.id && (
                          <button
                            onClick={() => handleAutologinClick(user)}
                            className="text-emerald-600 hover:text-emerald-900 dark:text-emerald-450 dark:hover:text-emerald-350"
                            title="Auto Login"
                          >
                            <ArrowRightOnRectangleIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl border border-slate-200 dark:border-slate-800 w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
              <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Username <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="username"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="e.g. janesmith"
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="e.g. jane@example.com"
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Password {!editingUser && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={editingUser ? "Leave empty to keep current" : "Minimum 8 characters"}
                  className="input-field"
                  required={!editingUser}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  System Role
                </label>
                <select
                  name="role"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="input-field"
                >
                  <option value="billing_officer">Billing Officer</option>
                  <option value="approver">Approver</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="reconciliation_officer">Reconciliation Officer</option>
                  <option value="auditor">Auditor</option>
                  <option value="it_support">IT Support</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="input-field"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="locked">Locked</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createUser.isLoading || updateUser.isLoading}
                  className="btn-primary"
                >
                  {createUser.isLoading || updateUser.isLoading ? 'Saving...' : 'Save User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
